import { useInfiniteQuery } from '@tanstack/react-query';
import {
  CHAT_SEARCH_MAX_QUERY_LENGTH,
  chatSearchMinQueryLength,
  isChatSearchDay,
  type ChatSearchHasFilter,
  type ChatSearchMatchMode,
} from '@fias/platform-resources';
import { api, ApiError, type ChatMessageResponse, type ChatMessageSearchFilters } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

/** The mode search panels open in — matches text anywhere, like most search boxes. */
export const CHAT_SEARCH_DEFAULT_MATCH_MODE: ChatSearchMatchMode = 'contains';

/** True for the server's "search matched too broadly to finish" refusal. */
export function isSearchTooBroadError(error: unknown): boolean {
  return error instanceof ApiError && error.code === 'SEARCH_TOO_BROAD';
}

/**
 * Why a search failed, in the terms the panels explain to the user:
 * - `tooBroad` — 422 `SEARCH_TOO_BROAD`, add words or a sender;
 * - `rateLimited` — 429, wait a moment;
 * - `unsupportedFilters` — a 400 on a request that carried a content or date
 *   filter, which an API older than this frontend refuses (its query schema
 *   is `.strict()`); the filters are the cause, not the words;
 * - `other` — anything else.
 */
export type ChatSearchErrorKind = 'tooBroad' | 'rateLimited' | 'unsupportedFilters' | 'other';

export function chatSearchErrorKind(
  error: unknown,
  request: Pick<ChatMessageSearchFilters, 'has' | 'since' | 'until'>,
): ChatSearchErrorKind | null {
  if (!error) return null;
  if (isSearchTooBroadError(error)) return 'tooBroad';
  if (error instanceof ApiError) {
    if (error.status === 429) return 'rateLimited';
    if (error.status === 400 && (request.has || request.since || request.until)) {
      return 'unsupportedFilters';
    }
  }
  return 'other';
}

/**
 * Everything a search panel can set besides the text. `null` / `undefined`
 * mean "not filtering on this". Dates are ISO 8601 instants already resolved
 * from the user's local days (see `searchDayBounds`).
 */
export interface ChatSearchFilters {
  /** Sender's username (no leading @). */
  senderUsername: string | null;
  matchMode: ChatSearchMatchMode;
  has: ChatSearchHasFilter | null;
  since: string | null;
  until: string | null;
}

export const EMPTY_CHAT_SEARCH_FILTERS: ChatSearchFilters = {
  senderUsername: null,
  matchMode: CHAT_SEARCH_DEFAULT_MATCH_MODE,
  has: null,
  since: null,
  until: null,
};

/**
 * Normalizes the raw search inputs shared by both search hooks: trims and caps
 * the text, treats text below the mode's minimum as absent (so "de" + a sender
 * still searches by sender rather than sitting idle), and reports whether
 * anything is searchable at all — text, a sender, or a content filter. A date
 * range alone is not a search (the server refuses it too).
 */
export function normalizeChatSearchInput(
  query: string,
  filters: ChatSearchFilters,
): { terms: string | undefined; request: ChatMessageSearchFilters; isQueryable: boolean } {
  const trimmed = query.trim().slice(0, CHAT_SEARCH_MAX_QUERY_LENGTH);
  const from = filters.senderUsername?.trim() || undefined;
  const terms = trimmed.length >= chatSearchMinQueryLength(filters.matchMode) ? trimmed : undefined;
  const has = filters.has ?? undefined;
  return {
    terms,
    request: {
      from,
      match: filters.matchMode,
      has,
      since: filters.since ?? undefined,
      until: filters.until ?? undefined,
    },
    isQueryable: !!terms || !!from || !!has,
  };
}

/** The filter set as one stable string for a query key. */
export function chatSearchFiltersKey(request: ChatMessageSearchFilters): string {
  return [
    request.from ?? '',
    request.match ?? '',
    request.has ?? '',
    request.since ?? '',
    request.until ?? '',
  ].join('|');
}

/**
 * ISO instants bounding a range of the user's LOCAL calendar days, half-open:
 * `after` is the start of that day, `before` is the start of that day (so the
 * day itself is excluded — Slack's `before:` convention). Either may be
 * absent. The server compares instants, so the user's timezone is honored
 * without the server knowing it.
 */
export function searchDayBounds(
  after: string | null,
  before: string | null,
): { since: string | null; until: string | null } {
  // A day that does not exist (`2030-02-30`) is no bound at all — never a
  // silent roll-over into March, which would narrow the search to a range the
  // user did not ask for.
  const startOfLocalDay = (day: string): string | null => {
    if (!isChatSearchDay(day)) return null;
    const [y, m, d] = day.split('-').map(Number);
    return new Date(y, m - 1, d).toISOString();
  };
  return {
    since: after ? startOfLocalDay(after) : null,
    until: before ? startOfLocalDay(before) : null,
  };
}

export interface UseChatMessageSearchResult {
  results: ChatMessageResponse[];
  /** Total matches in the channel, from the first page. */
  total: number;
  isLoading: boolean;
  isError: boolean;
  /** The server gave up because the search matched too broadly (422 SEARCH_TOO_BROAD). */
  isTooBroad: boolean;
  /** Why the search failed, when it did; null otherwise. */
  errorKind: ChatSearchErrorKind | null;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
}

/**
 * Search over one channel's messages, cursor-paginated. Backed by
 * `GET /arches/chat/channels/:channelId/messages/search`, which accepts text
 * terms, a sender username, a content filter, or any combination — so
 * "everything Bob wrote here" and "every file shared here" are searches with
 * no text at all — optionally bounded to a date range.
 *
 * Only runs while `enabled` (the panel is open) and at least one selector is
 * usable, so a closed panel or a half-typed word costs nothing. The caller is
 * responsible for debouncing `query`.
 */
export function useChatMessageSearch(
  channelId: string | null,
  query: string,
  filters: ChatSearchFilters,
  enabled: boolean,
  pageSize = 25,
): UseChatMessageSearchResult {
  const { terms, request, isQueryable } = normalizeChatSearchInput(query, filters);

  const result = useInfiniteQuery({
    queryKey: queryKeys.chat.messageSearch(
      channelId ?? '',
      terms ?? '',
      chatSearchFiltersKey(request),
    ),
    queryFn: ({ pageParam }) =>
      api.searchChatMessages(channelId!, terms ?? '', {
        ...request,
        limit: pageSize,
        before: pageParam as string | undefined,
      }),
    initialPageParam: undefined as string | undefined,
    // The server decides whether more exists (it over-fetches by one) rather
    // than the client inferring it from a count — a count-vs-page mismatch
    // caused by a concurrent delete would otherwise strand "Load more" forever.
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled: enabled && !!channelId && isQueryable,
    staleTime: 30_000,
  });

  return {
    results: result.data?.pages.flatMap((p) => p.messages) ?? [],
    // Only the first page carries a total; later pages report null.
    total: result.data?.pages[0]?.total ?? 0,
    // Nothing searchable isn't "loading" — it's idle, and the panel shows its
    // prompt rather than a spinner that would never resolve.
    isLoading: isQueryable && result.isLoading,
    isError: result.isError,
    isTooBroad: isSearchTooBroadError(result.error),
    errorKind: chatSearchErrorKind(result.error, request),
    hasNextPage: result.hasNextPage,
    isFetchingNextPage: result.isFetchingNextPage,
    fetchNextPage: () => void result.fetchNextPage(),
  };
}
